import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp } from 'lucide-preact';
import { useEffect, useState } from 'preact/hooks';

import { useCustomerLoyalty } from '@/context/LoyaltyContext';
import { useWidgetConfig } from '@/context/WidgetConfigContext';
import { COLORS } from '@/helpers/constants/theme';
import { capitalizeFirstLetter, getMessageLocationClasses } from '@/helpers/functions';
import { formatNumberWithCommas } from '@/helpers/functions/number';
import { useHoverBackground } from '@/hooks/useHoverBackground';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  E_FloatingWidgetMessageLocation,
  E_FloatingWidgetMobileMessageLocation,
  E_FloatingWidgetTriggerLocation,
} from '@/types/FloatingWidgetTrigger';
import type { I_MessageBubbleProps } from '@/types/MessageBubble';
import { cn } from '@/ui/utils';

export const MessageBubble = ({
  isWidgetOpen,
  openWidget,
  showMessage,
  setShowMessage,
  isHovered,
}: I_MessageBubbleProps) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  const { customerLoyalty } = useCustomerLoyalty();
  const loyaltyTier = customerLoyalty?.programs?.loyaltyTier;
  const currentTierNameEn = loyaltyTier?.currentTier?.nameEn;
  const flexiblePoints = customerLoyalty?.programs?.flexiblePoints;
  const MESSAGES = {
    unregistered: [
      '🔥 Join our loyalty program – unlock exclusive discounts!',
      "👋 You're missing out! Register now for rewards",
      '🚀 Exclusive deals await – sign up for loyalty rewards today!',
    ],
    registered: [
      `${loyaltyTier ? `⭐ You're ${currentTierNameEn && capitalizeFirstLetter(currentTierNameEn)} Tier! Enjoy ${loyaltyTier?.benefitsDescription}` : ''}`,
      `${flexiblePoints ? `🎯 You have ${formatNumberWithCommas(flexiblePoints?.availablePoints)} points to spend!` : ''}`,
      // `${customerLoyalty?.programs?.punchCard && `☕ 2 more coffees for a free drink!`}`, //TODO: Add it when the program is implemented
    ],
  };

  const messageList = customerLoyalty?.basicInfo?.enableId
    ? MESSAGES.registered
    : MESSAGES.unregistered;
  const currentMessage = messageList[currentMessageIndex % messageList.length];
  const isVisible = showMessage && !isWidgetOpen;

  const { widgetLocation, messageLocation, mobileMessageLocation, theme } = useWidgetConfig();
  const { backgroundColor = COLORS.BRAND_PRIMARY, textColor = COLORS.TEXT_INVERSE } =
    theme?.floatingButtonMessage ?? {};

  const isMobile = useIsMobile();

  const effectiveMessageLocation = isMobile ? mobileMessageLocation : messageLocation;

  const positionClasses = getMessageLocationClasses(widgetLocation, effectiveMessageLocation);

  const { backgroundColor: hoverBackground, hoverHandlers } = useHoverBackground({
    baseColor: backgroundColor,
  });

  useEffect(() => {
    if (isWidgetOpen) {
      setShowMessage(false);
      return;
    }

    const messageRotationInterval = setInterval(() => {
      if (!isHovered) {
        setShowMessage(false);
        setTimeout(() => {
          setCurrentMessageIndex((prev) => prev + 1);
          setShowMessage(true);
        }, 500);
      }
    }, 6000);

    return () => clearInterval(messageRotationInterval);
  }, [isHovered, messageList.length, isWidgetOpen]);

  useEffect(() => {
    if (isWidgetOpen) return;

    const messageFadeTimeout = setTimeout(() => {
      if (!isHovered) {
        setShowMessage(false);
      }
    }, 5000);

    return () => clearTimeout(messageFadeTimeout);
  }, [currentMessageIndex, isHovered, isWidgetOpen]);

  if (!showMessage || isWidgetOpen) return null;

  const renderArrow = () => {
    const baseClasses = 'absolute w-4 h-4 text-gray-900 fill-current';

    const isRightSide = widgetLocation === E_FloatingWidgetTriggerLocation.BOTTOM_RIGHT;

    switch (effectiveMessageLocation) {
      case E_FloatingWidgetMessageLocation.RIGHT:
        return (
          <ArrowLeft
            className={cn(baseClasses, '-left-2 top-1/2 -translate-y-1/2')}
            style={{ color: backgroundColor }}
          />
        );

      case E_FloatingWidgetMessageLocation.LEFT:
        return (
          <ArrowRight
            className={cn(baseClasses, '-right-2 top-1/2 -translate-y-1/2')}
            style={{ color: backgroundColor }}
          />
        );

      case E_FloatingWidgetMessageLocation.ABOVE:
      case E_FloatingWidgetMobileMessageLocation.ABOVE:
        return (
          <ArrowDown
            className={cn(baseClasses, 'bottom-0 -mb-2', isRightSide ? 'right-2' : 'left-2')}
            style={{ color: backgroundColor }}
          />
        );

      case E_FloatingWidgetMobileMessageLocation.BELOW:
        return (
          <ArrowUp
            className={cn(baseClasses, 'top-0 -mt-2', isRightSide ? 'right-2' : 'left-2')}
            style={{ color: backgroundColor }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={cn(
        'absolute transition-all duration-300',
        isVisible ? 'opacity-100' : 'opacity-0',
        positionClasses,
      )}
    >
      <div
        id='floating-widget-message'
        onClick={openWidget}
        {...hoverHandlers}
        className={cn(
          'rounded-lg px-4 py-3 shadow-lg',
          'cursor-pointer transition-colors',
          'relative w-max',
          isMobile ? 'max-w-[80vw]' : 'max-w-[1000px]',
        )}
        style={{
          backgroundColor: hoverBackground,
          color: textColor,
        }}
      >
        <h5 className='whitespace-normal break-words font-medium'>{currentMessage}</h5>
        {renderArrow()}
      </div>
    </div>
  );
};
